/**
 * MessageItem Component
 * Displays individual chat messages with interactions (copy, reactions, bet detection)
 *
 * TODO(refactor): Extract useTextSelection hook - handles selection state and menu positioning
 * TODO(refactor): interactionState has 5 properties - consider splitting by concern
 * TODO(perf): Memoize handlers passed to child components
 * TODO(test): Add tests for edge case - very long messages with horizontal scroll
 */

import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ThumbsUp, ThumbsDown, Copy, Check, PlusCircle, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BetDetectionButton from '../BetDetectionButton';
import { formatTableContent } from '../utils/tableFormatter';
import { markdownComponents } from '../utils/markdownConfig';
import useCopyToClipboard from '../hooks/useCopyToClipboard';

const MessageItem = memo(({ msg, handleReaction, userId }) => {
  const [interactionState, setInteractionState] = useState({
    showAddedSuccess: false,
    selectedText: '',
    showSelectionMenu: false,
    menuPosition: { top: 0, left: 0 },
    showBetDetection: false  // New state for bet detection toggle
  });

  const messageRef = useRef(null);
  const isUser = msg.sender === "user";

  // Use the reusable copy hook
  const { isCopied, copy } = useCopyToClipboard();

  // Memoized handlers to prevent re-creation
  const updateInteractionState = useCallback((updates) => {
    setInteractionState(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle text selection within the message
  // TODO(refactor): Extract to useTextSelection hook
  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection.toString().trim();

      if (text && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        if (messageRef.current && messageRef.current.contains(range.commonAncestorContainer)) {
          const rect = range.getBoundingClientRect();
          updateInteractionState({
            selectedText: text,
            showSelectionMenu: true,
            menuPosition: {
              top: rect.bottom + window.scrollY + 10,
              left: rect.left + window.scrollX + (rect.width / 2) - 50
            }
          });
        } else {
          updateInteractionState({ showSelectionMenu: false });
        }
      } else {
        updateInteractionState({ showSelectionMenu: false });
      }
    };

    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('touchend', handleSelection);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('touchend', handleSelection);
    };
  }, [updateInteractionState]);

  // Handle click outside to close selection menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (interactionState.showSelectionMenu && !e.target.closest('.selection-menu')) {
        updateInteractionState({ showSelectionMenu: false });
      }
    };

    if (interactionState.showSelectionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [interactionState.showSelectionMenu, updateInteractionState]);

  // Copy handler using the reusable hook
  const handleCopy = useCallback(() => {
    copy(msg.text);
  }, [copy, msg.text]);

  const handleAddToBetSlip = useCallback((text) => {
    if (window.addToBetSlip) {
      window.addToBetSlip(text);
      updateInteractionState({
        showAddedSuccess: true,
        showSelectionMenu: false
      });
      setTimeout(() => updateInteractionState({ showAddedSuccess: false }), 2000);
    }
  }, [updateInteractionState]);

  // TODO(perf): Memoize this handler
  const memoizedReactionHandler = useCallback((messageId, reaction) => {
    handleReaction(messageId, reaction);
  }, [handleReaction]);

  return (
    <motion.div
      className={`flex ${isUser ? "justify-end" : "justify-start"} py-1.5 w-full`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className={`rounded-2xl px-3.5 py-2.5 md:px-4 md:py-3 transition-all duration-200 relative group
          ${isUser ? 'max-w-[85%]' : 'w-full'} md:max-w-[75%]
          ${isUser
            ? "bg-[#2D6A4F]/30 text-white border border-emerald-500/30"
            : "bg-white/[0.10] text-white border border-white/[0.15]"
          } backdrop-filter backdrop-blur-sm shadow-sm`}
        style={{
          boxShadow: isUser
            ? '0 1px 2px rgba(45, 106, 79, 0.1)'
            : '0 1px 2px rgba(0, 0, 0, 0.1)'
        }}
        ref={messageRef}
      >
          <div className="text-[15px] md:text-[16px] font-light leading-relaxed break-words prose prose-invert prose-p:text-inherit prose-p:leading-relaxed prose-table:text-inherit">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {formatTableContent(msg.text) || ''}
            </ReactMarkdown>
          </div>

          {/* Floating selection menu */}
          <AnimatePresence>
            {interactionState.showSelectionMenu && (
              <motion.div
                className="selection-menu fixed bg-slate-800/90 backdrop-filter backdrop-blur-md border border-slate-700/50 rounded-lg shadow-xl p-2 z-50"
                style={{
                  top: interactionState.menuPosition.top,
                  left: interactionState.menuPosition.left
                }}
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <motion.button
                  onClick={() => handleAddToBetSlip(interactionState.selectedText)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-emerald-300 hover:bg-emerald-800/30 rounded transition-colors w-full"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <PlusCircle className="w-4 h-4" />
                  <span className="text-xs whitespace-nowrap">Add to Bet Slip</span>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bet Detection Button with manual trigger */}
          {!isUser && (
            <BetDetectionButton
              message={msg}
              isUser={isUser}
              manualTrigger={true}
              showBox={interactionState.showBetDetection}
            />
          )}

          {!isUser && (
            <div className="flex flex-wrap gap-2 mt-3.5">
              {/* Thumbs up button */}
              <motion.button
                onClick={() => memoizedReactionHandler(msg.id, 'thumbsUp')}
                className="p-1.5 rounded-full transition-colors"
                whileHover={{ scale: 1.1, backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                whileTap={{ scale: 0.95 }}
              >
                <ThumbsUp className={`w-4 h-4 transition-all duration-300 ${
                  msg.reaction === 'thumbsUp'
                    ? 'text-emerald-400 scale-110 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]'
                    : 'text-emerald-200/70 hover:text-emerald-300'
                }`} />
              </motion.button>

              {/* Thumbs down button */}
              <motion.button
                onClick={() => memoizedReactionHandler(msg.id, 'thumbsDown')}
                className="p-1.5 rounded-full transition-colors"
                whileHover={{ scale: 1.1, backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                whileTap={{ scale: 0.95 }}
              >
                <ThumbsDown className={`w-4 h-4 transition-all duration-300 ${
                  msg.reaction === 'thumbsDown'
                    ? 'text-emerald-400 scale-110 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]'
                    : 'text-emerald-200/70 hover:text-emerald-300'
                }`} />
              </motion.button>

              {/* Copy button */}
              <motion.button
                data-copy-message="true" // Data attribute for onboarding targeting
                onClick={handleCopy}
                className="p-1.5 rounded-full transition-colors"
                whileHover={{ scale: 1.1, backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                whileTap={{ scale: 0.95 }}
                title="Copy message"
              >
                {isCopied ? (
                  <Check className="w-4 h-4 text-emerald-400" />
                ) : (
                  <Copy className="w-4 h-4 text-emerald-200/70 hover:text-emerald-300" />
                )}
              </motion.button>

              {/* Bet Detection trigger button */}
              <motion.button
                data-bet-detection="true" // Data attribute for onboarding targeting
                onClick={() => updateInteractionState({ showBetDetection: !interactionState.showBetDetection })}
                className="flex items-center gap-1 px-2 py-1 rounded-full transition-colors"
                whileHover={{ scale: 1.05, backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                whileTap={{ scale: 0.95 }}
                title="Show betting picks"
              >
                <Tag className={`w-3.5 h-3.5 transition-all duration-300 ${
                  interactionState.showBetDetection
                    ? 'text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]'
                    : 'text-emerald-200/70 hover:text-emerald-300'
                }`} />
                <span className={`text-xs transition-all duration-300 ${
                  interactionState.showBetDetection
                    ? 'text-emerald-400'
                    : 'text-emerald-200/70 group-hover:text-emerald-300'
                }`}>BookLink</span>
              </motion.button>

              {/* PickExtractor button */}
              {/* <PickExtractorButton text={msg.text}/> */}
            </div>
          )}
        </motion.div>
    </motion.div>
  );
});

MessageItem.displayName = 'MessageItem';

export default MessageItem;
